import React, { useState, useCallback } from 'react';
import { MapView } from './map/MapView';
import { StatusBar } from './components/StatusBar';
import { Toolbar, type ToolId } from './components/Toolbar';
import { PanelContainer } from './components/PanelContainer';
import { AviationOverlayPanel } from './panels/AviationOverlayPanel';
import { BasemapPanel } from './panels/BasemapPanel';
import { AirportSearchPanel } from './panels/AirportSearchPanel';
import { WeatherPanel } from './panels/WeatherPanel';
import { AirspacePanel } from './panels/AirspacePanel';
import { ApproachPlatesPanel } from './panels/ApproachPlatesPanel';
import { BookmarksPanel } from './panels/BookmarksPanel';
import { DrawingPanel } from './panels/DrawingPanel';
import { TerrainProfilePanel } from './panels/TerrainProfilePanel';
import { CoordinatePanel } from './panels/CoordinatePanel';
import { DistanceBearingPanel } from './panels/DistanceBearingPanel';
import { TrackLogPanel } from './panels/TrackLogPanel';
import { SettingsPanel } from './panels/SettingsPanel';
import { CqlFilterPanel } from './panels/CqlFilterPanel';
import { FlightPlanPanel } from './panels/FlightPlanPanel';
import { NavLogViewerPanel } from './panels/NavLogViewerPanel';
import { ExportImportPanelWrapper } from './panels/ExportImportPanelWrapper';
import { PlanListPanel } from './panels/PlanListPanel';
import { LoginPanel } from './panels/LoginPanel';
import { FlightReviewPanel } from './panels/FlightReviewPanel';
import { SpatialAnalysisPanel } from './panels/SpatialAnalysisPanel';
import { DownloadPanel } from './panels/DownloadPanel';
import { MapProvider } from './map/MapContext';
import { OgcServicesProvider } from './services/OgcServicesContext';
import { AuthProvider } from './services/AuthContext';
import { FlightPlanProvider } from './services/FlightPlanContext';
import { ThreeDProvider, useThreeD } from './map/ThreeDViewContext';
import { ThreeDViewContainer } from './map/ThreeDViewContainer';
import { SplitView } from './components/SplitView';

import './styles/layout.css';

const ThreeDTogglePanel: React.FC = () => {
  const { enabled, setEnabled, loading, ready, unavailableReason } = useThreeD();
  return (
    <div>
      <div className="panel-section">
        <div className="panel-section-title">3D Photorealistic View</div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
          Pair the 2D chart with a live Cesium globe showing Google
          Photorealistic 3D Tiles and Cesium Ion World Terrain, with
          bidirectional camera sync.
        </p>
        <button
          className={enabled ? 'btn btn-primary' : 'btn btn-secondary'}
          disabled={!!unavailableReason && !enabled}
          onClick={() => setEnabled(!enabled)}
          style={{ width: '100%' }}
        >
          {enabled ? 'Disable 3D pane' : 'Enable 3D pane'}
        </button>
        <div style={{ marginTop: 8, fontSize: 11 }}>
          Status:{' '}
          <strong style={{ color: ready ? 'var(--success, #22C55E)' : loading ? 'var(--warning, #F59E0B)' : 'var(--text-muted)' }}>
            {loading ? 'Loading…' : ready ? 'Ready' : enabled ? 'Initialising' : 'Off'}
          </strong>
        </div>
        {unavailableReason && (
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--danger, #EF4444)' }}>
            {unavailableReason}
          </div>
        )}
      </div>
    </div>
  );
};

const PANEL_MAP: Record<ToolId, { title: string; Component: React.FC }> = {
  flightplan:  { title: 'Flight Plan',         Component: FlightPlanPanel },
  navlog:      { title: 'Navigation Log',      Component: NavLogViewerPanel },
  planlist:    { title: 'Saved Plans',          Component: PlanListPanel },
  export:      { title: 'Export / Import',      Component: ExportImportPanelWrapper },
  overlays:    { title: 'Aviation Overlays',   Component: AviationOverlayPanel },
  basemap:     { title: 'Basemap',             Component: BasemapPanel },
  airports:    { title: 'Airport Search',      Component: AirportSearchPanel },
  weather:     { title: 'Weather',             Component: WeatherPanel },
  airspace:    { title: 'Airspace',            Component: AirspacePanel },
  plates:      { title: 'Approach Plates',     Component: ApproachPlatesPanel },
  filter:      { title: 'CQL Data Filter',    Component: CqlFilterPanel },
  bookmarks:   { title: 'Bookmarks',           Component: BookmarksPanel },
  drawing:     { title: 'Drawing',             Component: DrawingPanel },
  terrain:     { title: 'Terrain Profile',     Component: TerrainProfilePanel },
  coordinates: { title: 'Coordinates',         Component: CoordinatePanel },
  measure:     { title: 'Distance & Bearing',  Component: DistanceBearingPanel },
  tracklog:    { title: 'Track Log',           Component: TrackLogPanel },
  settings:    { title: 'Settings',            Component: SettingsPanel },
  login:       { title: 'Authentication',       Component: LoginPanel },
  review:      { title: 'Flight Review',         Component: FlightReviewPanel },
  spatial:     { title: 'Spatial Analysis',       Component: SpatialAnalysisPanel },
  download:    { title: 'Aviation Downloads',      Component: DownloadPanel },
  threeD:      { title: '3D View',                Component: ThreeDTogglePanel },
};

const AppShell: React.FC = () => {
  const [activeTool, setActiveTool] = useState<ToolId | null>(null);
  const handleToolSelect = useCallback((tool: ToolId) => {
    setActiveTool((prev) => (prev === tool ? null : tool));
  }, []);
  const handlePanelClose = useCallback(() => setActiveTool(null), []);
  const panel = activeTool ? PANEL_MAP[activeTool] : null;
  const { enabled: threeDEnabled } = useThreeD();

  return (
    <div className="app-shell">
      <StatusBar />
      <div className="app-main">
        <Toolbar activeTool={activeTool} onSelect={handleToolSelect} />
        {panel && (
          <PanelContainer title={panel.title} onClose={handlePanelClose}>
            <panel.Component />
          </PanelContainer>
        )}
        <div className="map-container">
          <SplitView
            splitEnabled={threeDEnabled}
            leftChild={<MapView />}
            rightChild={<ThreeDViewContainer />}
          />
        </div>
      </div>
    </div>
  );
};

export const App: React.FC = () => {
  return (
    <AuthProvider>
      <OgcServicesProvider>
        <MapProvider>
          <FlightPlanProvider>
            <ThreeDProvider>
              <AppShell />
            </ThreeDProvider>
          </FlightPlanProvider>
        </MapProvider>
      </OgcServicesProvider>
    </AuthProvider>
  );
};
